import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Zap, Brain, Workflow, Shield, Settings, Play, Database, Users } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function ZapierLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Find Memara in Zapier
Search for Memara in Zapier app directory

# 2. Create your first Zap
Connect trigger app to Memara action

# 3. Configure memory creation
Set up data mapping for memory fields

# 4. Turn on your Zaps
Start automated memory workflows`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=ouW739Hx7v8', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="zapier"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-50 shadow" aria-label="Zapier Logo">
          <Icon icon="logos:zapier-icon" width={48} height={48} />
        </span>
      }
      title="Zapier Integration"
      description="Enterprise workflow automation with persistent memory infrastructure. Connect 5,000+ business applications to create intelligent memory capture workflows that transform any app trigger into contextual knowledge storage with multi-tenant security."
      features={[
        { icon: <Zap className="h-4 w-4 text-orange-600" />, title: 'Lightning-Fast Memory Automation', description: 'Instant memory creation from 5,000+ Zapier-supported applications with sub-second response times.' },
        { icon: <Brain className="h-4 w-4 text-orange-600" />, title: 'Intelligent Workflow Processing', description: 'AI-powered memory creation from complex workflow data with semantic understanding and context extraction.' },
        { icon: <Workflow className="h-4 w-4 text-orange-600" />, title: 'Universal App Connectivity', description: 'Connect any Zapier-supported application to memory creation with comprehensive data mapping and transformation.' },
        { icon: <Database className="h-4 w-4 text-orange-600" />, title: 'Enterprise Data Management', description: 'Multi-tenant memory isolation with enterprise-grade security and compliance for business automation workflows.' },
        { icon: <Users className="h-4 w-4 text-orange-600" />, title: 'Team Workflow Memory', description: 'Shared organizational memory with role-based access controls and collaborative workflow intelligence.' },
        { icon: <Shield className="h-4 w-4 text-orange-600" />, title: 'Reliable Business Integration', description: 'Production-grade reliability with error handling, retry logic, and comprehensive monitoring for business-critical workflows.' },
      ]}
      setupSteps={[
        'Find Memara in Zapier app directory',
        'Create automated workflow with memory actions',
        'Configure intelligent data mapping and memory schemas',
        'Activate Zaps for continuous memory automation',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-orange-200 text-orange-600 hover:bg-orange-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-orange-200 text-orange-600 hover:bg-orange-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Settings className="w-4 h-4 mr-2" />,
        },
      ]}
      screenshots={[
        {
          id: 'zapier-app-directory',
          title: 'Find Memara in Zapier',
          description: 'Search and connect Memara from the Zapier app directory with 5,000+ integration options',
          placeholder: 'Zapier app directory showing Memara integration with connection options',
          badge: 'Step 1'
        },
        {
          id: 'zap-builder',
          title: 'Build Memory Workflows',
          description: 'Create intelligent Zaps that transform app triggers into contextual memory storage',
          placeholder: 'Zapier workflow builder showing trigger-to-memory automation setup',
          badge: 'Automation'
        },
        {
          id: 'enterprise-monitoring',
          title: 'Enterprise Monitoring',
          description: 'Monitor workflow performance with enterprise-grade analytics and error handling',
          placeholder: 'Zapier dashboard showing Memara integration analytics and success metrics',
          badge: 'Analytics'
        }
      ]}
      videoUrl="https://www.youtube.com/watch?v=ouW739Hx7v8"
      microcopy="Trusted by automation engineers, business process specialists, and enterprise teams using Zapier for workflow automation and business integration."
      trustedBy="Zapier, Enterprise Automation Teams, and more."
      testimonial="Memara's Zapier integration transforms our business automation workflows into intelligent memory systems that capture and contextualize every important business process and data transformation across all our applications."
    />
  );
} 